import { useEffect } from "react";
import { useDialog } from "src/components/Modalservice/Dialogservice";
import { useAtom } from "jotai";
import screenAudioEditService, { currentWaveDataAtom } from "../ScreenAudioEditService";
import classes from '../player.module.scss';
import LnIcon from "src/components/LnIcon";
import { useI18n } from "src/utils/lni18n";
import { useLocation, useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { CompleteMultipartUploadCommandOutput } from "@aws-sdk/client-s3";
import { RecorderDialog, RecordingAction } from "../../AudioRecorder/RecorderDialog";
import recorderService from "../../AudioRecorder/Recoderservice";
import FileSaverDialog from "src/components/FileUpload/FileSaverDialog";
import { useAlert } from "src/components/Modalservice/UseAlert";
import { useConfirm } from "src/components/Modalservice/UseConfirm";


const WaveEditorControls = (props: { children?: any, className?: string }) => {

    const { languageService: t } = useI18n();
    const [screendata] = useAtom(currentWaveDataAtom);
    const dialogPortal = useDialog();
    const navigate = useNavigate();
    const { isPlaying, isDirty, haveSelection, canRedo, canUndo,
        hasStereo } = screendata;

    const alert = useAlert();

    const { state } = useLocation();
    const editRecording = state?.editRecording ?? false;

    const confirm = useConfirm();


    const startRecord = () => {

        const mono = screenAudioEditService.waves.length === 1;

        recorderService.initializeAudio(mono);
        var p = dialogPortal({
            factory: (onSubmit, onCancel) => {

                return <RecorderDialog onCancel={onCancel} onSubmit={onSubmit} />
            },
            size: "recorder"
        });
        p.then((result: RecordingAction) => {
            if (recorderService.recordedData) {
                screenAudioEditService.insertAudio(recorderService.getDataAsAudioSequences());
                recorderService.Reset();
            }


        }).catch(() => {
            ensureCancelRecord();
        });
    }

    const ensureCancelRecord = () => {
        return confirm("md", t.getText("lectureitem.record.sound"),
            <div className="preserve-white" >{t.getText("edit.dirty")}</div>)
            .then((answ: boolean) => {
                if (answ) {
                    screenAudioEditService.insertAudio(recorderService.getDataAsAudioSequences());
                }
            }).catch(() => { })
            .finally(() => { recorderService.Reset(); });

    }

    const quitEdit = () => {

        if (isDirty || editRecording) {


            var p = confirm("md", t.getText("save.before.close.without.question"),
                <div className="preserve-white" >{t.getText("save.before.close")}</div>);

            p.then(async (answ: boolean) => {
                if (answ) {
                    const saver = saveWave();
                    if (saver) {
                        try {
                            await saver;
                            backToScreen();
                        }
                        catch (e) {
                            console.error("Save Wave failed", e);
                            await alert("md", t.getText("error"), <div className="preserve-white" >{t.getText("generalerror")}</div>);
                            backToScreen();
                        }
                    }
                }
                else {
                    backToScreen();
                }
            }).catch(() => { });
        }
        else {
            backToScreen();
        }

    }


    const saveWave = () => {
        const saveData = screenAudioEditService.saveAudio();
        if (!saveData) {
            alert("md", t.getText("error"), <div className="preserve-white" >{t.getText("generalerror")}. No data to save</div>);
            return undefined;
        }

        const promise = saveData.promise.then((d: CompleteMultipartUploadCommandOutput) => {
            return d;
        });

        dialogPortal({
            factory: (onSubmit, onCancel) => {
                return <FileSaverDialog onCancel={onCancel} />
            },
            size: "md"
        }).catch(() => {
            saveData.killer && saveData.killer();
        });

        return promise;
    }

    const backToScreen = async () => {
        screenAudioEditService.playStop();
        screenAudioEditService.resetData();
        navigate(`./../../`, { state: { nostart: true } });
    }

    const handlePlayPauseClick = (all: boolean) => {
        screenAudioEditService.playToggle(all);
    }

    useEffect(() => {
        window.addEventListener("keydown", handleKeyEvent, true);
        window.addEventListener("copy", copy, true);
        window.addEventListener("paste", paste, true);
        window.addEventListener("cut", cut, true);

        return () => {
            window.removeEventListener("keydown", handleKeyEvent, true);
            window.removeEventListener("copy", copy, true);
            window.removeEventListener("paste", paste, true);
            window.removeEventListener("cut", cut, true);
        }
    }, [])

    const handleKeyEvent = (e: KeyboardEvent) => {

        switch (e.key) {
            case " ": // space
                screenAudioEditService.playToggle(e.ctrlKey);
                break;

            case "a":
                if (e.ctrlKey) {
                    screenAudioEditService.selectAll();
                    e.preventDefault()
                }
                break;

            case "ArrowRight":
                screenAudioEditService.selectToTheRight(e.ctrlKey, e.shiftKey);
                break;

            case "ArrowLeft":
                screenAudioEditService.selectToTheLeft(e.ctrlKey, e.shiftKey);
                break;

            case "y":
                if (e.ctrlKey) {
                    screenAudioEditService.redo();
                }
                break;

            case "z":
                if (e.ctrlKey) {
                    screenAudioEditService.undo();
                }
                break;

            case "Backspace":
            case "Delete":
                screenAudioEditService.del();
                break;
            // case "Escape":
            //     quitButton.current && quitButton.current.click();
            //     break;
        }
    }

    const cut = () => {
        screenAudioEditService.cut();
    }

    const copy = () => {
        screenAudioEditService.copy();
    }
    const paste = () => {
        screenAudioEditService.paste();
    }

    return <>

        <div className={`${classes.buttonsbar} ${props.className} align-self-end  mx-auto my-2 pb-1 pl-3 pr-3 d-flex align-items-center`}>
            <div className="d-flex align-items-center">
                <button title={t.getText("close")} className="btn btn-link mr-3" onClick={() => quitEdit()}>
                    <LnIcon name="off" />
                </button>
                <div className="d-flex">

                    <button title={t.getText("play.all") + " (Ctrl + Space)"} className={`${classes.roundButton} ${classes.blueButton} btn btn-link d-flex align-items-center`} onClick={() => handlePlayPauseClick(true)}>
                        {isPlaying ? <LnIcon name="pause" /> : <LnIcon name="play" />}
                    </button>

                    <button title={t.getText("play") + " (Space)"} className={classes.roundButton + " btn btn-link d-flex align-items-center mr-2"} onClick={() => handlePlayPauseClick(false)}>
                        {isPlaying ? <LnIcon name="pause" /> : <LnIcon name="play" />}
                    </button>

                </div>

                <button title={t.getText("zoom100")} className="btn btn-link ml-3" onClick={(e) => { screenAudioEditService.zoomToFit() }}>
                    <LnIcon className="filter-white" name="zoom-out" />
                </button>

                <button disabled={!haveSelection} title={t.getText("zoomin")} className="btn btn-link" onClick={(e) => { screenAudioEditService.zoomIntoSelection() }}>
                    <LnIcon className="filter-white" name="zoom-in" />
                </button>

                <button disabled={!haveSelection} title={t.getText("hivolume")} className="btn btn-link ml-3" onClick={(e) => { screenAudioEditService.filterGain(+3) }}>
                    <LnIcon className="filter-white" name="volume-plus" />
                </button>
                <button disabled={!haveSelection} title={t.getText("lovolume")} className="btn btn-link" onClick={(e) => { screenAudioEditService.filterGain(-3) }}>
                    <LnIcon className="filter-white" name="volume-minus" />
                </button>
                <button title={t.getText("normalize")} className="btn btn-link " onClick={(e) => { screenAudioEditService.filterNormalize() }}>
                    <LnIcon name="volume" />
                </button>

                <button disabled={!haveSelection} title={t.getText("fadein")} className="btn btn-link ml-3" onClick={(e) => { screenAudioEditService.filterFadeIn() }}>
                    <LnIcon className="filter-white" name="arrow-up-right" />
                </button>
                <button disabled={!haveSelection} title={t.getText("fadeout")} className="btn btn-link" onClick={(e) => { screenAudioEditService.filterFadeOut() }}>
                    <LnIcon className="filter-white" name="arrow-down-right" />
                </button>

                <button disabled={!canUndo} title={t.getText("undo")} className="btn btn-link ml-3" onClick={(e) => { screenAudioEditService.undo() }}>
                    <LnIcon className="filter-white" name="undo" />
                </button>

                <button disabled={!canRedo} title={t.getText("redo")} className="btn btn-link" onClick={(e) => { screenAudioEditService.redo() }}>
                    <LnIcon className="filter-white" name="redo" />
                </button>

                {hasStereo &&
                    <button title={t.getText("merge.to.mono")} className="btn btn-link ml-3" onClick={(e) => { screenAudioEditService.MergeToMono() }}>
                        <LnIcon className="filter-white" name="share" />
                    </button>
                }

                <button disabled={!isDirty} title={t.getText("save.short")} className="btn btn-link mx-3" onClick={(e) => { saveWave() }}>
                    <LnIcon className="filter-white" name="download2" />
                </button>

                <button title={t.getText("record")} className="btn btn-link mx-3" onClick={startRecord}>
                    <LnIcon className="filter-white" name="mic" />
                </button>

                <UncontrolledDropdown onClick={() => { }}>
                    <DropdownToggle tag="button" className="btn btn-link mr-xs-2">
                        <LnIcon name="minus" className="filter-white" title={t.getText("insert.silence")} />
                    </DropdownToggle>


                    <DropdownMenu className="w-auto" >
                        <div className={"d-flex flex-column justify-content-right "}>
                            <button onClick={(e) => screenAudioEditService.insertSilence(0.25)} className={"btn btn-link  " + classes.speeds} >
                                <span>0.25 s</span>
                            </button>

                            <button onClick={(e) => screenAudioEditService.insertSilence(0.5)} className={"btn btn-link " + classes.speeds} >
                                <span>0.50 s</span>
                            </button>
                            <button onClick={(e) => screenAudioEditService.insertSilence(1)} className={"btn btn-link " + classes.speeds} >
                                <span>1.00 s</span>
                            </button>
                            <button onClick={(e) => screenAudioEditService.insertSilence(2)} className={"btn btn-link " + classes.speeds} >
                                <span>2.00 s</span>
                            </button>
                            <button onClick={(e) => screenAudioEditService.insertSilence(5)} className={"btn btn-link " + classes.speeds} >
                                <span>5.00 s</span>
                            </button>
                        </div>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        </div>
    </>
}

export default WaveEditorControls;